/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/scheduleProposal/useScheduleProposal.ts
 * PURPOSE: Hook cho màn "Đăng ký ca làm việc" (web) — danh mục mã ca, đề xuất
 *          tuần của chính mình, gửi đề xuất, và LỊCH CHÍNH THỨC của tuần đó.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + Rule 3 (Zero-Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: WFM — nhân viên tự đăng ký ca
 * - Impact: Ngày đã có ca chính thức thì KHÔNG cho đăng ký đè
 * - Stakeholders: Nhân viên ca
 *
 * @technical-architecture
 * - Layer: Feature hook (TanStack Query)
 * - Pattern: query theo tuần + mutation invalidate đúng tuần
 * - Dependencies: services/api/scheduleProposalApi, services/api/client
 *
 * @cross-references
 * - Backend: backend/app/api/v2/endpoints/schedule_proposal.py
 * - Backend (lịch chính thức): backend/app/api/v1/schedule.py get_my_schedule
 * - Bản mobile: mobile/src/hooks/useScheduleProposal.ts + useSchedule.ts
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { scheduleProposalApi } from "@/services/api/scheduleProposalApi";
import type { SubmitWeekProposalRequest } from "@/services/api/scheduleProposalApi";
import { apiRequest } from "@/services/api/config";
import { useAuthStore } from "@/store/auth";

export const SCHEDULE_PROPOSAL_KEYS = {
  shiftCodes: ["schedule-proposal", "shift-codes"] as const,
  week: (weekStart: string) => ["schedule-proposal", "week", weekStart] as const,
  officialWeek: (weekStart: string) => ["schedule-proposal", "official-week", weekStart] as const,
};

/** Ca chính thức trong tuần — chỉ các field màn này cần. */
export interface OfficialWeekShift {
  id: string;
  date: string; // YYYY-MM-DD
  start_time: string; // "HH:MM:SS"
  end_time: string;
  shift_code?: string | null;
  status?: string;
}

export function useProposalShiftCodes() {
  return useQuery({
    queryKey: SCHEDULE_PROPOSAL_KEYS.shiftCodes,
    queryFn: () => scheduleProposalApi.getShiftCodes(),
    staleTime: 5 * 60_000, // danh mục mã ca đổi rất ít
    retry: 1,
  });
}

export function useMyWeekProposal(weekStart: string) {
  return useQuery({
    queryKey: SCHEDULE_PROPOSAL_KEYS.week(weekStart),
    queryFn: () => scheduleProposalApi.getMyWeekProposal(weekStart),
    enabled: Boolean(weekStart),
    staleTime: 15_000,
    retry: 1,
  });
}

/**
 * Lịch CHÍNH THỨC của tuần (quản lý xếp trực tiếp, hoặc đề xuất đã duyệt).
 *
 * Vì sao cần: `GET /schedules/proposals/week` CHỈ trả ca do chính nhân viên đề
 * xuất (source=EMPLOYEE). Tuần mà quản lý đã xếp sẵn sẽ hiện trống trơn ở màn
 * đăng ký như thể chưa ai xếp gì — đúng lỗi mobile đã gặp và đã sửa bằng cách
 * gọi thêm nguồn lịch chính thức (ScheduleProposalScreen.tsx). Web dùng lại
 * cùng endpoint mà màn "Lịch làm việc" đang dùng.
 *
 * ⚠️ `/my-schedule` CỐ Ý trả cả ca `cancelled` (màn Lịch cần hiện "Nghỉ (ca đã
 * huỷ)"), nên phải LỌC trước khi coi là "ngày đã có ca" — nếu không, một ca đã
 * huỷ sẽ khoá mất ngày đó, nhân viên không đăng ký được.
 */
export function useOfficialWeekSchedule(weekStart: string, weekEnd: string) {
  const { accessToken } = useAuthStore();
  return useQuery({
    queryKey: SCHEDULE_PROPOSAL_KEYS.officialWeek(weekStart),
    enabled: Boolean(weekStart && weekEnd),
    staleTime: 30_000,
    retry: 1,
    queryFn: async (): Promise<OfficialWeekShift[]> => {
      const params = new URLSearchParams({ start_date: weekStart, end_date: weekEnd });
      const raw = await apiRequest<OfficialWeekShift[] | { schedules: OfficialWeekShift[] }>(
        `/schedules/my-schedule?${params}`,
        { token: accessToken || undefined },
      );
      const rows = Array.isArray(raw) ? raw : raw?.schedules ?? [];
      return rows.filter((s) => (s.status ?? "").toLowerCase() !== "cancelled");
    },
  });
}

export function useSubmitWeekProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SubmitWeekProposalRequest) =>
      scheduleProposalApi.submitWeekProposal(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: SCHEDULE_PROPOSAL_KEYS.week(variables.week_start) });
    },
  });
}
